import {
  Building2,
  ChevronDown,
  Clock3,
  Target,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';
import type {
  CheckInObjective,
  ObjectiveState,
  ObjectiveStatus,
  TreatmentCheckInV2,
} from '@/services/api/treatmentCheckInV2Api';

import { Badge } from '../../ui/badge';
import { Card } from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';

interface ObjectiveRailProps {
  objectiveState: ObjectiveState | null;
  callPriorities: TreatmentCheckInV2['call_priorities'];
  isUpdatingObjectives?: boolean;
  onStatusChange: (objectiveId: string, status: ObjectiveStatus) => void;
  className?: string;
  mobile?: boolean;
}

function getDueBadge(objective: CheckInObjective): string | null {
  const dueAt = objective.task_context?.due_at;
  if (dueAt) {
    const due = new Date(dueAt);
    const now = new Date();
    if (!Number.isNaN(due.getTime())) {
      if (due.getTime() < now.getTime()) return 'Overdue';
      const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays <= 3) return 'Due soon';
    }
  }
  if (objective.kind === 'diagnostic_follow_up') return '30+ days';
  if (objective.priority_band === 'now') return 'Now';
  return null;
}

function stripHtmlSummary(summary: string): string {
  return summary
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+\n/g, '\n')
    .replace(/\n\s+/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function fallbackObjectives(
  objectiveState: ObjectiveState | null,
  callPriorities: TreatmentCheckInV2['call_priorities'],
): CheckInObjective[] {
  if (objectiveState?.objectives?.length) {
    const byId = new Map(objectiveState.objectives.map((objective) => [objective.id, objective]));
    const visible = objectiveState.visible_objective_ids
      .map((objectiveId) => byId.get(objectiveId))
      .filter((objective): objective is CheckInObjective => Boolean(objective));
    if (visible.length > 0) return visible;
    return [...objectiveState.objectives].sort((a, b) => a.rank - b.rank);
  }

  return (callPriorities?.objectives ?? []).map((label, index) => ({
    id: `legacy-${index}`,
    label,
    short_label: label,
    status: 'open',
    kind: 'follow_up',
    priority_band: 'soon',
    pinned: false,
    rank: index + 1,
    client_relevant: true,
    freshness: 'snapshot_only',
    primary_source: 'prior_call',
    source_bundle: [],
    source_ids: [],
    task_context: null,
    provider_context: null,
    why_now: '',
    done_when: null,
    why_this_matters: '',
    operator_state: null,
    ai_state: null,
  }));
}

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
    {children}
  </h3>
);

export const ObjectiveRail = ({
  objectiveState,
  callPriorities,
  isUpdatingObjectives = false,
  onStatusChange,
  className,
  mobile = false,
}: ObjectiveRailProps) => {
  const objectives = useMemo(
    () => fallbackObjectives(objectiveState, callPriorities),
    [callPriorities, objectiveState],
  );
  const unresolvedCount = objectives.filter((objective) => objective.status !== 'done').length;
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const displayedObjectives = objectives.slice(0, 5);
  const preview = objectives.find((objective) => objective.status !== 'done') ?? objectives[0];

  const content = (
    <div className="flex flex-col gap-3 p-3">
      <section>
        <div className="flex items-center gap-2">
          <Target className="size-4 text-muted-foreground" />
          <SectionLabel>Objectives</SectionLabel>
          {objectives.length > 0 && (
            <Badge variant="secondary" className="text-[10px]">
              {unresolvedCount} open
            </Badge>
          )}
        </div>

        {objectives.length === 0 ? (
          <p className="mt-3 text-sm font-medium text-muted-foreground">
            No objectives yet.
          </p>
        ) : (
          <div className="mt-2 space-y-1">
            {displayedObjectives.map((objective) => {
              const dueBadge = getDueBadge(objective);
              const isChecked = objective.status === 'done';
              return (
                <div
                  key={objective.id}
                  className={cn(
                    'rounded-md border px-2 py-1.5 transition-colors',
                    isChecked ? 'border-border/60 bg-muted/20' : 'border-border bg-background',
                  )}
                >
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={isChecked}
                      disabled={isUpdatingObjectives}
                      className="mt-0.5 size-4 rounded-[4px]"
                      onCheckedChange={(checked) =>
                        onStatusChange(objective.id, checked === true ? 'done' : 'open')
                      }
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p
                            className={cn(
                              'text-[13px] font-medium leading-5',
                              isChecked && 'text-muted-foreground line-through',
                            )}
                          >
                            {objective.label}
                          </p>
                          {(objective.why_now || objective.provider_context?.provider_name) && (
                            <details className="mt-1">
                              <summary className="cursor-pointer list-none text-[11px] font-medium text-muted-foreground hover:text-foreground">
                                Why?
                              </summary>
                              <div className="mt-1 space-y-1 text-[11px] font-medium leading-snug text-muted-foreground">
                                {objective.why_now && <p>{objective.why_now}</p>}
                                {objective.provider_context?.provider_name && (
                                  <p className="inline-flex items-center gap-1">
                                    <Building2 className="size-3" />
                                    {objective.provider_context.provider_name}
                                  </p>
                                )}
                              </div>
                            </details>
                          )}
                        </div>
                        <div className="ml-auto flex shrink-0 flex-col items-end gap-1">
                          {dueBadge && (
                            <Badge
                              variant={objective.priority_band === 'now' ? 'default' : 'outline'}
                              className="text-[10px]"
                            >
                              <Clock3 className="mr-1 size-2.5" />
                              {dueBadge}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {callPriorities?.last_check_in && (
        <section>
          <div className="rounded-lg border border-border/70 bg-muted/25 p-3">
            <p className="text-sm font-semibold text-foreground">Prior Summary</p>
            <p className="mt-1 text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
              {stripHtmlSummary(callPriorities.last_check_in.summary)}
            </p>
          </div>
        </section>
      )}

      <section className="space-y-2">
        <SectionLabel>Provider Context</SectionLabel>
        {(callPriorities?.providers ?? []).length > 0 ? (
          <div className="space-y-2">
            {callPriorities?.providers?.map((provider) => (
              <div
                key={provider.provider_id}
                className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
              >
                <p className="text-sm font-semibold">{provider.name}</p>
                <p className="text-xs font-medium text-muted-foreground">
                  {[provider.specialty, provider.status, provider.frequency].filter(Boolean).join(' · ')}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm font-medium text-muted-foreground">No provider context yet.</p>
        )}
      </section>
    </div>
  );

  if (!mobile) {
    return (
      <Card
        className={cn(
          'w-full shrink-0 overflow-y-auto rounded-none border-r border-t-0 border-b-0 border-l-0 shadow-none lg:basis-[35%] lg:max-w-[35%] lg:min-w-[35%]',
          className,
        )}
      >
        {content}
      </Card>
    );
  }

  return (
    <Card className={cn('relative z-10 shrink-0 overflow-hidden', className)}>
      <button
        type="button"
        onClick={() => setMobileExpanded((current) => !current)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Target className="size-4 text-muted-foreground" />
            <span className="text-sm font-semibold">Objectives</span>
            <Badge variant="secondary" className="text-[10px]">
              {unresolvedCount} open
            </Badge>
          </div>
          {preview && (
            <p className="line-clamp-1 text-xs font-medium text-muted-foreground">
              {preview.label}
            </p>
          )}
        </div>
        <ChevronDown className={cn('size-4 transition-transform', mobileExpanded && 'rotate-180')} />
      </button>
      {mobileExpanded && <div className="border-t">{content}</div>}
    </Card>
  );
};
